import React, { Component, ErrorInfo, ReactNode } from "react";
import { classifyError } from "@/lib/errorClassifier";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

function detectChunkError(error: Error | null): boolean {
  if (!error) return false;
  const msg = (error.message || "").toLowerCase();
  const name = (error.name || "").toLowerCase();

  // Vite / webpack chunk load patterns (Chrome, Firefox, Safari, mobile)
  if (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("unable to preload") ||
    // Vite 5+ / Safari: "Importing a module script failed."
    msg.includes("importing a module script") ||
    // Chrome when chunk 404s: "Failed to load module script: ..."
    msg.includes("failed to load module script") ||
    // Firefox: "Loading module from ... was blocked"
    msg.includes("loading module from") ||
    // Webpack ChunkLoadError
    name.includes("chunkloaderror") ||
    // Generic fetch failure on a /assets/ URL (chunk 404 after SW cleanup)
    (msg.includes("failed to fetch") && (msg.includes("/assets/") || msg.includes(".js") || msg.includes(".css")))
  ) {
    return true;
  }

  // Check error.cause for nested fetch failures (Vite wraps some errors)
  const cause = (error as any).cause;
  if (cause instanceof Error) return detectChunkError(cause);

  return false;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isChunkError: detectChunkError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { category } = classifyError(error);
    const isChunk = detectChunkError(error);

    console.error(`[ErrorBoundary] ${category}:`, error.message);

    if (isChunk) {
      this.handleClearCacheAndReload();
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  handleReload = () => {
    globalThis.location.reload();
  };

  // force=true: chamado pelo botão do usuário — ignora o throttle
  // force=false (default): chamado automaticamente no componentDidCatch — respeita throttle para evitar loop infinito
  handleClearCacheAndReload = async (force = false) => {
    const RECOVERY_KEY = "__glucofy_eb_recovery";
    if (!force) {
      const last = sessionStorage.getItem(RECOVERY_KEY);
      if (last && Date.now() - Number(last) < 30_000) return;
    }
    sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));

    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // Also clear IndexedDB asset cache (image blobs) to prevent stale data
      if (window.indexedDB) {
        await new Promise<void>((resolve) => {
          const req = window.indexedDB.deleteDatabase("app-asset-cache");
          req.onsuccess = req.onerror = req.onblocked = () => resolve();
        });
      }
    } catch (e) {
      console.warn("[ErrorBoundary] Cache cleanup failed:", e);
    }
    // Redireciona para home com cache-bust — não volta para a página que crashou
    const homeUrl = globalThis.location.origin + "/?_r=" + Date.now();
    globalThis.location.replace(homeUrl);
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      // Detect language: prefer user's saved preference, fall back to browser language,
      // then default to "es". This ensures EN/DE users see the error in their language
      // even on first visit before they've set a preference.
      const getLang = (): string => {
        if (typeof localStorage !== "undefined") {
          const stored = localStorage.getItem("app_language");
          if (stored === "en" || stored === "es" || stored === "de") return stored;
        }
        if (typeof navigator !== "undefined" && navigator.language) {
          const nav = navigator.language.toLowerCase();
          if (nav.startsWith("en")) return "en";
          if (nav.startsWith("de")) return "de";
        }
        return "es";
      };
      const lang = getLang();
      const isEs = lang === "es";
      const isDe = lang === "de";

      const title = this.state.isChunkError
        ? (isEs ? "Actualización disponible" : isDe ? "Update verfügbar" : "Update available")
        : (isEs ? "Algo salió mal" : isDe ? "Etwas ist schiefgelaufen" : "Something went wrong");
      const desc = this.state.isChunkError
        ? (isEs ? "Una nueva versión está disponible. La app se recargará automáticamente." : isDe ? "Eine neue Version ist verfügbar. Die App wird automatisch neu geladen." : "A new version is available. The app will reload automatically.")
        : (isEs ? "Ocurrió un error inesperado. Intenta recargar la app." : isDe ? "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie, neu zu laden." : "An unexpected error occurred. Please try reloading.");
      const clearLabel = isEs ? "Limpiar caché y recargar" : isDe ? "Cache leeren und neu laden" : "Clear cache and reload";
      const retryLabel = isEs ? "Intentar de nuevo" : isDe ? "Erneut versuchen" : "Try again";

      return (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-background">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">{title}</h2>
            <p className="text-muted-foreground text-sm mb-3">{desc}</p>
            {this.state.error && (
              <p className="text-[11px] font-mono bg-muted/60 rounded-lg px-3 py-2 mb-4 text-left break-all" style={{ color: "#8C7355" }}>
                {this.state.error.message || String(this.state.error)}
              </p>
            )}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => this.handleClearCacheAndReload(true)}
                className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-base active:scale-[0.98] transition-transform"
              >
                {clearLabel}
              </button>
              <button
                onClick={this.handleRetry}
                className="w-full py-3 rounded-xl border border-border text-foreground font-medium text-sm active:scale-[0.98] transition-transform"
              >
                {retryLabel}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
